import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { FormHelperText } from '@mui/material';
import { colors } from 'common/constants';

interface Props<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
    name: string;
    label?: string;
    placeholder?: string;
    helperText?: React.ReactNode;
    endAdornment?: React.ReactNode;
    enableEnterToAdd?: boolean;
    blurOnSelect?: boolean;
    customOnInputChange?: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
}

export default function BaseAutocomplete<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
>({
    name,
    label,
    placeholder,
    helperText,
    endAdornment,
    enableEnterToAdd,
    customOnInputChange,
    blurOnSelect = false,
    ...other
}: Omit<Props<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
    const { control, setValue, trigger, register } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <>
                    <Autocomplete
                        {...field}
                        popupIcon={other.popupIcon}
                        onChange={(event, newValue) => {
                            setValue(name, newValue, {
                                shouldValidate: true,
                            });
                            // TODO: blur field after select
                            if (blurOnSelect) {
                                trigger(name);
                            }
                        }}
                        disableCloseOnSelect
                        renderInput={(params) => (
                            <TextField
                                {...register(`${name}Input`)}
                                label={label}
                                // name={`${name}Input`}
                                placeholder={placeholder}
                                error={!!error}
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: endAdornment,
                                }}
                                onKeyDown={(event) => {
                                    event.stopPropagation();

                                    if (
                                        enableEnterToAdd &&
                                        event.key === 'Enter'
                                    ) {
                                        event.preventDefault();

                                        const value = (
                                            event.target as HTMLInputElement
                                        ).value;

                                        if (value.trim() !== '') {
                                            const newValue = {
                                                label: value,
                                                value: value,
                                                email: value,
                                            };

                                            const currentValue = Array.isArray(
                                                field.value
                                            )
                                                ? field.value
                                                : [];

                                            const isDuplicate =
                                                currentValue.some(
                                                    (item: { value: string }) =>
                                                        item.value ===
                                                        newValue.value
                                                );

                                            if (!isDuplicate) {
                                                setValue(
                                                    name,
                                                    [...currentValue, newValue],
                                                    { shouldValidate: true }
                                                );
                                            }

                                            (
                                                event.target as HTMLInputElement
                                            ).value = '';
                                        }
                                    }
                                }}
                                onChange={(event) => {
                                    event.stopPropagation();
                                    customOnInputChange &&
                                        customOnInputChange(event);
                                }}
                            />
                        )}
                        tabIndex={-1}
                        {...other}
                    />
                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: '14px',
                                marginLeft: 0,
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </>
            )}
        />
    );
}
